import { useTranslation } from 'react-i18next';
import type { GroupId } from 'balus-common';
import { Modal } from '@framework/ui/elements/Modal';
import { useAIUsageStatus } from '@group/hooks/useAIUsageStatus';
import { useCurrentUserIsLeviiStaff } from '@user/UserEntity/useCurrentUserIsLeviiStaff';
import { AIUsageDonutChart } from './AIUsageDonutChart';
import { AIUsageLimitReachedNotice } from './AIUsageLimitReachedNotice';

type Props = {
  isOpen: boolean;
  onClose(): void;
  groupId: GroupId;
};

export const AIUsageModal: React.FC<Props> = ({ isOpen, onClose, groupId }: Props) => {
  const { t } = useTranslation();
  const { status, loading } = useAIUsageStatus(groupId, { skip: !isOpen });
  const isLeviiStaff = useCurrentUserIsLeviiStaff();
  const isOverLimit = !isLeviiStaff && status !== null && status.usedRatio >= 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="p-6">
      <h2 className="mb-4 text-lg font-bold">{t('framework.aiUsage.title')}</h2>
      {status ? (
        <div className="flex flex-col items-center gap-3">
          <AIUsageDonutChart usedRatio={status.usedRatio} />
          <p className="text-xs text-gray-500">{t('framework.aiUsage.description')}</p>
          {isOverLimit && <AIUsageLimitReachedNotice groupId={groupId} className="w-full" />}
        </div>
      ) : (
        <div className="py-8 text-center text-sm text-gray-500">
          {loading ? t('framework.aiUsage.loading') : t('common.message.loadFailed')}
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <button onClick={onClose} className="rounded-md px-4 py-2 text-gray-600 hover:bg-gray-100">
          {t('common.action.close')}
        </button>
      </div>
    </Modal>
  );
};
